import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { loginUrl, changePasswordUrl, ASSETS } from '../BaseURL';
import { useFacility } from '../context/FacilityContext';
import { swalError, toastSuccess } from '../utils/swal';
import '../styles/public.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { facility } = useFacility();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // first-login password change state
  const [step, setStep] = useState('login'); // 'login' | 'set-password'
  const [pendingUser, setPendingUser] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const res = await axios.post(loginUrl, { username: username.trim(), password });
      if (res.data?.success) {
        const user = res.data.user;
        if (user.first_login) {
          setPendingUser(user);
          setStep('set-password');
        } else {
          localStorage.setItem('user', JSON.stringify(user));
          toastSuccess(`Welcome back, ${user.full_name?.split(' ')[0] || 'there'}`);
          const dest = location.state?.from && location.state.from.startsWith('/management')
            ? location.state.from : '/management';
          navigate(dest, { replace: true });
        }
      } else {
        const msg = res.data?.message || 'Login failed';
        setError(msg);
        swalError('Sign-in failed', msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed';
      setError(msg);
      swalError('Sign-in failed', msg);
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(changePasswordUrl, { user_id: pendingUser.id, new_password: newPw });
      if (res.data?.success) {
        const updatedUser = { ...pendingUser, first_login: 0 };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        toastSuccess(`Password set! Welcome, ${pendingUser.full_name?.split(' ')[0] || 'there'}`);
        const dest = location.state?.from && location.state.from.startsWith('/management')
          ? location.state.from : '/management';
        navigate(dest, { replace: true });
      } else {
        const msg = res.data?.message || 'Could not update password';
        setError(msg);
        swalError('Password change failed', msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not update password';
      setError(msg);
      swalError('Password change failed', msg);
    } finally {
      setBusy(false);
    }
  };

  const brandName = facility?.name || 'POS Ghana';
  const brandMark = (facility?.name?.[0] || 'P').toUpperCase();

  return (
    <div className="login-wrap">
      {/* ─── Left art panel ─── */}
      <aside className="login-art">
        <div className="login-art-dots" aria-hidden />

        <Link to="/" className="pub-brand" aria-label="Back to home">
          {facility?.logo ? (
            <img src={`${ASSETS}${facility.logo}`} alt={brandName} className="brand-logo" />
          ) : (
            <span className="mark">{brandMark}</span>
          )}
          <span className="name">{brandName}</span>
        </Link>

        <div className="login-art-text">
          <div className="login-art-eyebrow">
            <span className="pulse" />Secure staff portal
          </div>
          <h2>Welcome <em>back.</em></h2>
          <p className="quote">
            One sign-in. Every branch, every register, every report — running on the
            same modern Point of Sale.
          </p>

          <div className="login-art-preview" aria-hidden>
            <div className="lap-row">
              <span className="lap-eyebrow">Today across branches</span>
              <span className="lap-trend">▲ 14%</span>
            </div>
            <div className="lap-num">GH₵ 12,840</div>
            <div className="lap-sub">124 sales · 4 branches active · 0 alerts</div>
            <div className="lap-bars">
              <div style={{ height: '55%' }} />
              <div style={{ height: '70%' }} />
              <div style={{ height: '40%' }} />
              <div style={{ height: '92%' }} />
              <div style={{ height: '65%' }} />
              <div style={{ height: '82%' }} />
              <div style={{ height: '74%' }} />
              <div style={{ height: '88%' }} />
            </div>
          </div>
        </div>

        <div className="login-art-foot">
          <span className="dot" />
          {facility?.name
            ? <>Configured for <strong style={{ color: '#fff', marginLeft: 4 }}>{facility.name}</strong></>
            : 'Modern Point of Sale'}
        </div>
      </aside>

      {/* ─── Right form panel ─── */}
      <section className="login-form-wrap">
        {step === 'login' ? (
          <form className="login-card" onSubmit={submit}>
            <div className="login-card-head">
              <h3>Staff sign in</h3>
              <p className="sub">Use your username or email and password.</p>
            </div>

            {error && <div className="notice">{error}</div>}

            <div className="f-row">
              <label className="f-label" htmlFor="login-username">Username or email</label>
              <input
                id="login-username"
                className="f-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                required
                placeholder="e.g. jane.kofi or jane@shop.com"
              />
            </div>

            <div className="f-row">
              <label className="f-label" htmlFor="login-password">Password</label>
              <div className="login-pw">
                <input
                  id="login-password"
                  className="f-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="Your password"
                />
                <button
                  type="button"
                  className="toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button className="btn-primary login-card-submit" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in →'}
            </button>

            <div className="login-card-divider">Need help?</div>

            <div className="login-card-help">
              <span className="ic">?</span>
              <div>
                <strong style={{ color: 'var(--ink)' }}>Forgot your password?</strong>{' '}
                Ask your Super Administrator to reset it from the Users & Access panel.
              </div>
            </div>

            <div className="login-card-foot">
              Not part of this team? <Link to="/">Back to the website</Link>
            </div>
          </form>
        ) : (
          <form className="login-card" onSubmit={submitNewPassword}>
            <div className="login-card-head">
              <h3>Set your password</h3>
              <p className="sub">
                Welcome, <strong>{pendingUser?.full_name?.split(' ')[0]}</strong>. Your account was
                created with a temporary password. Please set a new one to continue.
              </p>
            </div>

            {error && <div className="notice">{error}</div>}

            <div className="f-row">
              <label className="f-label" htmlFor="new-password">New password</label>
              <div className="login-pw">
                <input
                  id="new-password"
                  className="f-input"
                  type={showNewPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoFocus
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  className="toggle"
                  onClick={() => setShowNewPw((v) => !v)}
                  aria-label={showNewPw ? 'Hide password' : 'Show password'}
                >
                  {showNewPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="f-row">
              <label className="f-label" htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                className="f-input"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                required
                placeholder="Repeat your new password"
              />
            </div>

            <button className="btn-primary login-card-submit" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Set password & continue →'}
            </button>

            <div className="login-card-foot" style={{ marginTop: 16 }}>
              Wrong account?{' '}
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
                onClick={() => { setStep('login'); setPendingUser(null); setNewPw(''); setConfirmPw(''); setError(null); }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default LoginPage;
